import {
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Spinner,
  Center,
  Text,
  Badge,
  Wrap,
  WrapItem,
  Avatar,
  HStack,
  useColorModeValue,
  Heading,
  Icon,
  Tooltip,
  Flex
} from '@chakra-ui/react';
import { MdAccountBalanceWallet, MdFavoriteBorder } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { UserModel } from 'models';
import { UserAccountStatus, UserRole } from 'models/User/User';
import { NavigationService } from 'services';
import EmptyState from 'components/EmptyState/EmptyState';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';

export interface UserTableProps {
  users: UserModel[];
  isLoading?: boolean;
}

export default function UserTable({ users, isLoading = false }: UserTableProps) {
  const headerBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const navigate = useNavigate();

  const handlePortfolioClick = (user: UserModel) => {
    if (user.getPortfolioId()) {
      navigate(NavigationService.getPortfolioDetailsPath(user.getPortfolioId()));
    }
  };

  const handleWishlistClick = (user: UserModel) => {
    const wishlistId = user.getWishlistId();
    if (wishlistId) {
      navigate(NavigationService.getWishlistDetailsPath(wishlistId));
    }
  };

  if (isLoading) {
    return (
      <Center py={12}>
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!users.length) {
    return (
      <EmptyState
        header="No Users"
        description="There are no users to display right now."
        showButton={false}
      />
    );
  }

  return (
    <Box overflowX="auto" fontSize="sm">
      <Table variant="simple">
        <Thead bg={headerBg}>
          <Tr>
            <Th>Username</Th>
            <Th>Email</Th>
            <Th>Country</Th>
            <Th>Account Status</Th>
            <Th>Roles</Th>
            <Th>Joined</Th>
            <Th>View</Th>
          </Tr>
        </Thead>
        <Tbody>
          {users.map(user => (
            <Tr key={user.id}>
              <Td>
                <HStack spacing={3}>
                  <Avatar
                    size="sm"
                    name={user.userName || user.name || user.email}
                    src={user.image || undefined}
                  />
                  <Heading fontSize="sm">{user.userName || '—'}</Heading>
                </HStack>
              </Td>
              <Td>{user.email || '—'}</Td>
              <Td>{user.country?.code ? getUnicodeFlagIcon(user.country?.code) : '—'}</Td>
              <Td>
                <Badge colorScheme={getStatusColor(user.accountStatus)}>
                  {user.accountStatus}
                </Badge>
              </Td>
              <Td>
                {user.roles?.length ? (
                  <Wrap spacing={1} shouldWrapChildren>
                    {user.roles.map(role => (
                      <WrapItem key={`${user.id}_${role}`}>
                        <Badge colorScheme={getRoleColor(role)} variant="subtle">
                          {role}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                ) : (
                  <Text color="gray.500">—</Text>
                )}
              </Td>
              <Td>{user.timestamps.createdAt ? user.timestamps.createdAt.format('YYYY-MM-DD') : '—'}</Td>
              <Td>
                <Flex gap={3} alignItems="center">
                  <Tooltip label="View Portfolio" hasArrow>
                    <Box>
                      <Icon
                        as={MdAccountBalanceWallet}
                        w={5}
                        h={5}
                        cursor={!!user.getPortfolioId() ? "pointer" : "not-allowed"}
                        color={'gray.500'}
                        _hover={!!user.getPortfolioId() ? { color: "blue.500" } : {}}
                        onClick={() => handlePortfolioClick(user)}
                      />
                    </Box>
                  </Tooltip>
                  <Tooltip label="View Wishlist" hasArrow>
                    <Box>
                      <Icon
                        as={MdFavoriteBorder}
                        w={5}
                        h={5}
                        cursor={!!user.getWishlistId() ? "pointer" : "not-allowed"}
                        color={'gray.500'}
                        _hover={!!user.getWishlistId() ? { color: "pink.500" } : {}}
                        onClick={() => handleWishlistClick(user)}
                      />
                    </Box>
                  </Tooltip>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

function getStatusColor(status: UserAccountStatus): string {
  switch (status) {
    case UserAccountStatus.CONFIRMED:
      return 'green';
    case UserAccountStatus.BLOCKED:
      return 'red';
    case UserAccountStatus.PENDING:
    default:
      return 'yellow';
  }
}

function getRoleColor(role: UserRole): string {
  switch (role) {
    case UserRole.BASIC:
      return 'red';
    case UserRole.ADMIN:
      return 'purple';
    case UserRole.BASIC_ADMIN:
      return 'blue';
    case UserRole.USER:
    default:
      return 'cyan';
  }
}
